import React from 'react';
import { Mail, Phone, MapPin, MessageCircle, Send, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { COMPANY } from '../data/mock';
import { useContactForm } from '../hooks/useContactForm';

const SERVICE_OPTIONS = [
  'Virtual Assistants',
  'Customer Support Teams',
  'AI Automation',
  'Backend Operations',
  'Sales & Outreach',
  'Executive Assistance',
  'Technical Teams',
  'Marketing Teams',
  'Virtual Receptionists',
  'Not sure yet',
];

const CONTACT_CHANNELS = [
  { icon: Mail, title: 'Email', value: COMPANY.email, sub: 'Replies within 24 hours' },
  { icon: MessageCircle, title: 'WhatsApp', value: COMPANY.whatsapp, sub: 'Mon\u2013Sat, 9 AM \u2013 9 PM IST' },
  { icon: Phone, title: 'Phone', value: COMPANY.phone, sub: 'Talk to a strategist' },
  { icon: MapPin, title: 'Offices', value: COMPANY.locations.join(', '), sub: 'India-first, globally available' },
];

const Field = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="block text-xs text-white/60 mb-1.5 uppercase tracking-wider">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-[#0a1018] border border-[#1AB6FF]/25 focus:border-[#1AB6FF] outline-none rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/30"
    />
  </div>
);

const ContactHero = () => (
  <section className="relative pt-36 pb-16 overflow-hidden">
    <div className="absolute inset-0 bg-radial-glow pointer-events-none" />
    <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
    <div className="relative max-w-4xl mx-auto px-6 text-center">
      <p className="text-[#1AB6FF] font-semibold uppercase tracking-[0.2em] text-xs mb-4">Contact</p>
      <h1 className="font-display font-bold text-5xl md:text-6xl leading-[1.05]">
        Let&rsquo;s build your <span className="gradient-text">ops engine.</span>
      </h1>
      <p className="mt-6 text-white/65 text-lg max-w-2xl mx-auto">
        Book a free 30-minute strategy call. We&rsquo;ll audit your current operations and show you exactly where AI + dedicated teams can save you time and money.
      </p>
    </div>
  </section>
);

const ChannelCard = ({ channel }) => (
  <div className="card-tech rounded-2xl p-6 flex items-start gap-4">
    <div className="w-12 h-12 rounded-lg bg-[#1AB6FF]/10 border border-[#1AB6FF]/25 flex items-center justify-center shrink-0">
      <channel.icon size={22} className="text-[#1AB6FF]" />
    </div>
    <div>
      <p className="font-display font-semibold text-white">{channel.title}</p>
      <p className="text-[#1AB6FF] mt-1">{channel.value}</p>
      <p className="text-white/55 text-xs mt-1">{channel.sub}</p>
    </div>
  </div>
);

const CalendarCard = () => (
  <div className="card-tech rounded-2xl p-6 flex items-start gap-4">
    <div className="w-12 h-12 rounded-lg bg-[#1AB6FF]/10 border border-[#1AB6FF]/25 flex items-center justify-center shrink-0">
      <Calendar size={22} className="text-[#1AB6FF]" />
    </div>
    <div>
      <p className="font-display font-semibold text-white">Prefer a quick call?</p>
      <p className="text-white/55 text-sm mt-1">Pick a slot on our calendar. Free 30-min strategy session.</p>
      <button onClick={() => toast.success('Calendar mocked \u2014 will open Calendly in production.')} className="mt-3 btn-cyan rounded-full px-4 py-2 text-xs font-semibold">
        Book a slot
      </button>
    </div>
  </div>
);

const ContactChannels = () => (
  <div className="lg:col-span-2 space-y-5">
    {CONTACT_CHANNELS.map((c) => (
      <ChannelCard key={c.title} channel={c} />
    ))}
    <CalendarCard />
  </div>
);

const ContactForm = () => {
  const { form, update, submit } = useContactForm();
  return (
    <form onSubmit={submit} className="lg:col-span-3 card-tech rounded-2xl p-7 lg:p-9">
      <h2 className="font-display font-bold text-2xl text-white">Tell us about your business</h2>
      <p className="text-white/55 text-sm mt-1">We&rsquo;ll respond within one business day.</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="Your name *" value={form.name} onChange={update('name')} placeholder="Aarav Sharma" />
        <Field label="Work email *" type="email" value={form.email} onChange={update('email')} placeholder="you@company.in" />
        <Field label="Phone / WhatsApp" value={form.phone} onChange={update('phone')} placeholder="+91 98xxx xxxxx" />
        <Field label="Company" value={form.company} onChange={update('company')} placeholder="Your company name" />
        <div className="sm:col-span-2">
          <label className="block text-xs text-white/60 mb-1.5 uppercase tracking-wider">What do you need?</label>
          <select value={form.service} onChange={update('service')} className="w-full bg-[#0a1018] border border-[#1AB6FF]/25 focus:border-[#1AB6FF] outline-none rounded-md px-3 py-2.5 text-sm text-white">
            {SERVICE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-white/60 mb-1.5 uppercase tracking-wider">Tell us more *</label>
          <textarea value={form.message} onChange={update('message')} rows={5} placeholder="What ops are eating up your time?" className="w-full bg-[#0a1018] border border-[#1AB6FF]/25 focus:border-[#1AB6FF] outline-none rounded-md px-3 py-2.5 text-sm text-white resize-none" />
        </div>
      </div>

      <button type="submit" className="mt-6 btn-cyan rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">
        Send Message <Send size={16} />
      </button>
    </form>
  );
};

const ContactPage = () => (
  <div>
    <ContactHero />
    <section className="py-12 lg:py-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-8">
        <ContactChannels />
        <ContactForm />
      </div>
    </section>
  </div>
);

export default ContactPage;
